/* eslint-disable @typescript-eslint/require-await */
// @ ts-strict-ignore
/**
 * Connection service
 * @module Connection
 */

import { RBError } from 'rainbow-web-sdk/models/common';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { Subject, Subscription } from 'rxjs';
import { SdkLaunchConfig } from 'rainbow-web-sdk/services/launcher/config/sdkLaunchConfig';
import { Service } from 'rainbow-web-sdk/services/service';
import { AUTH_STATUS, AUTO_AUTH_TYPES, AuthService, AuthServiceEvents } from './auth.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { filter } from 'rxjs/operators';
import { ConnectedUser } from 'rainbow-web-sdk/models';
import { BubbleService } from '../bubbles';
import { BUBBLE_SVC, BubbleServiceRB } from '../bubbles/bubble.service';
import { UtilitiesService } from '../utilities/utilities.service';
import { SubscribeService } from './subscribe.service';

/** @internal */
export const CONNECTION_SVC = 'ConnectionService';

export enum ConnectionStateEnum {
    NONE = 'NONE',
    CONNECTING = 'CONNECTING',
    CONNECTED = 'CONNECTED',
    RECONNECTING = 'RECONNECTING',
    DISCONNECTED = 'DISCONNECTED',
}

export enum DisconnectionReasonEnum {
    UNLOGGED = 'UNLOGGED',
    NO_NETWORK = 'NO_NETWORK',
    CHANGE_PWD_ERROR = 'CHANGE_PWD_ERROR',
    THIRD_PARTY_CHANGE_PWD = '3RD_PARTY_CHANGE_PWD',
    AUTH_TOKEN_EXPIRE = 'TOKEN_EXPIRE',
}

export enum ConnectionTokenState {
    AUTH_TOKEN_WILL_EXPIRE = 'AUTH_TOKEN_WILL_EXPIRE',
    AUTH_TOKEN_RENEW = 'AUTH_TOKEN_RENEW',
}

export class ConnectionProgress {
    private constructor(public index: number, public length: number, public label: string) { }
    public static create(index: number, length: number, label: string): ConnectionProgress { return new ConnectionProgress(index, length, label); }
}

export class ConnectionState {
    private constructor(public state: ConnectionStateEnum, public reason?: DisconnectionReasonEnum, public progress?: ConnectionProgress) { }
    public static create<T extends ConnectionStateEnum>(state: T, reason?: T extends ConnectionStateEnum.DISCONNECTED ? DisconnectionReasonEnum : never, progress?: ConnectionProgress): ConnectionState { return new ConnectionState(state, reason, progress); }
}

export type ConnectionServiceEventHandler = (event: RBEvent) => any;

/**
 * ConnectionService service events
 * Can be listened via connectionService.subscribe() API
 */
export enum ConnectionServiceEvents {

    /**
     * @eventProperty RAINBOW_ON_CONNECTION_STATE_CHANGE
     * This RB event is send when the connectionService state change.
     */
    RAINBOW_ON_CONNECTION_STATE_CHANGE = 'RAINBOW_ON_CONNECTION_STATE_CHANGE',

    /**
     * @eventProperty RAINBOW_ON_TOKEN_STATE_CHANGE
     * This RB event is send when the connectionService token state change.
     */
    RAINBOW_ON_TOKEN_STATE_CHANGE = 'RAINBOW_ON_TOKEN_STATE_CHANGE'
}

export interface ConnectionService {

    /**
     * Register a evenbt handler function for when the connectionService state change.
     * @param handler - The handler function to register.
     * @param eventName - (optional) the name of the event to subscribe on.
     */
    subscribe(handler: ConnectionServiceEventHandler, eventName?: string): Subscription;

    /**
     * Allow to start the RainbowSDK.
     * @returns a promise that resolve when RainbowSDK is started and ready to use.
     * Return the authenticated contact if the autolog success.
     */
    start(): Promise<ConnectedUser | undefined>;

    /**
     * Allow to stop the RainbowSDK.
     * @returns a promise that resolve when RainbowSDK is stopped.
     */
    stop(): Promise<void>;

    /**
     * Allow to sign-on to Rainbow from a user account that already exists.
     * @param login - Rainbow hosted user login.
     * @param password - Rainbow hosted user password.
     * @param rememberMe - (default: false) When set to true, user token will be stored in local storage for further connections.
     */
    logon(login: string, password: string, rememberMe: boolean): Promise<ConnectedUser>;

    /**
     * Allow to sign-on with JWT token to Rainbow from a user account that already exists.
     * @param token - Rainbow user existing JWT token.
     */
    logonWithToken(token: string): Promise<ConnectedUser>;

    /**
     * Permits to log-on as "guest" in a bubble
     * @param bubblelink - The bubble link.
     * @param rememberMe - when set to true, the auth token will be stored in localStorage for further connections.
     */
    logonWithBubbleLink(bubblelink: string, rememberMe?: boolean): Promise<ConnectedUser>;

    /**
     * Use this method to update the OAuth token which is being used. It is
     * obligatory to update the token after each renewing. If the token is not
     * renewed, an event AUTH_TOKEN_EXPIRE will be emitted and the user will be disconnected
     * @param token - String representing the new AuthToken received after the renewing
     * @returns A promise that resolve when token is renewed.
     */
    setRenewedToken(token: string): Promise<void>;

    /**
     * Allow to sign-on with JWT token to Rainbow from a user account that already exists.
     * @returns A promise that resolve when user is log-out.
     */
    logout(): Promise<void>;

    /**
     * Get the current connection state with the Rainbow Cloud Services
     * @returns The connection state
     */
    getState(): ConnectionState;

    /**
     * Get the current token associated with the connection
     * @returns The token
     */
    getToken(): string;

    /**
     * Get the splatform server connection
     * @returns The server
     */
    getServer(): string;
}

/**
 * @internal
 */
export class ConnectionServiceRB extends Service implements ConnectionService {

    private eventSubject: Subject<RBEvent>;
    private connectionState: ConnectionState;

    private authService: AuthService = AuthService.getInstance();
    private loggerService: LoggerService = LoggerService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private subscribeService: SubscribeService = SubscribeService.getInstance();
    private launchConfiguration: SdkLaunchConfig = SdkLaunchConfig.create();
    private servicesStarted: boolean = false;
    private servicesStarting: boolean = false;
    private subscriptions: Subscription[] = [];

    public autoLogin: boolean = false;
    public progressEvents: boolean = false;

    public static getInstance(): ConnectionServiceRB { return Service.create(CONNECTION_SVC, ConnectionServiceRB); }
    public static build(): ConnectionServiceRB { return new ConnectionServiceRB(); }

    private constructor() {
        super();
        this.connectionState = ConnectionState.create(ConnectionStateEnum.NONE);
        this.eventSubject = new Subject<RBEvent>();
    }

    public subscribe(handler: ConnectionServiceEventHandler, eventNames?: string | string[]): Subscription {
        const observable = eventNames
            ? this.eventSubject.pipe(filter((event: RBEvent) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.eventSubject;

        return observable.subscribe((event: RBEvent) => {
            try { handler(event); }
            catch (error) { this.loggerService.error(`[ConnectionService] subscribe () -- error -- ${error.message}`); }
        });
    }

    public async start(): Promise<ConnectedUser | undefined> {
        this.loggerService.info(`[ConnectionService] start (autoAuth: ${this.autoLogin})`);
        this.servicesEventListeners();
        if (this.autoLogin) {
            this.updateConnectionState(ConnectionStateEnum.CONNECTING);
            const autoAuthResult = await this.authService.autoAuth();
            if (autoAuthResult.status === AUTH_STATUS.Failure) {
                switch (autoAuthResult.type) {
                    case AUTO_AUTH_TYPES.KeyChain:
                    case AUTO_AUTH_TYPES.CommandLine:
                    case AUTO_AUTH_TYPES.NoAuth:
                    default: break;
                }
                this.loggerService.info(`[ConnectionService] start () -- autoAuth -- failure`);
                this.updateConnectionState(ConnectionStateEnum.DISCONNECTED, DisconnectionReasonEnum.UNLOGGED);
                return undefined;
            }
            await this.startServices();
            return this.contactService.userContact;
        }
        return undefined;
    }

    public async stop(): Promise<void> {
        try {
            await this.logout();
        }
        catch (error) {
            this.loggerService.info(`[ConnectionService] stop () -- failure -- ${error.message}`);
            throw error;
        }
    }

    public async logon(login: string, password: string, rememberMe: boolean = false): Promise<ConnectedUser> {
        try {
            await this.authService.logon(login, password, rememberMe);
            this.loggerService.info(`[ConnectionService] login () -- success`);
            this.updateConnectionState(ConnectionStateEnum.CONNECTING);
            await this.startServices();
            if (!this.contactService.userContact) throw new Error('No connected user');
            return this.contactService.userContact;
        }
        catch (error) {
            this.loggerService.info(`[ConnectionService] login () -- failure -- ${error.message}`);
            throw error;
        }
    }

    public async logonWithToken(token: string): Promise<ConnectedUser> {
        try {
            await this.authService.logonWithToken(token);
            this.loggerService.info(`[ConnectionService] loginWithToken () -- success`);
            this.updateConnectionState(ConnectionStateEnum.CONNECTING);
            await this.startServices();
            return this.contactService.userContact;
        }
        catch (error) {
            this.loggerService.info(`[ConnectionService] loginWithToken () -- failure -- ${error.message}`);
            throw error;
        }
    }

    public async logonWithBubbleLink(bubbleLink: string, rememberMe: boolean = false): Promise<ConnectedUser> {
        const bubbleService = MainService.getInstance().inject<BubbleService>(BUBBLE_SVC);
        if (!bubbleService) throw new Error();
        const bubbleLinkCode = bubbleLink.split('/')[3]?.split('?')[0] || '';
        this.loggerService.info(`[ConnectionService] logonWithBubbleLink (bubbleLink: ${bubbleLink}, bubbleLinkCode: ${bubbleLinkCode})`);

        try {
            // Check accessCode validity
            await (bubbleService as BubbleServiceRB).checkOpenInviteIdValidity(bubbleLinkCode);

            // Create guest user credentials
            const dummyEmail = `__guest__${UtilitiesService.getInstance().randomString()}@dropme.com`;
            const dummyPassword = `@X0x${UtilitiesService.getInstance().randomString()}`;
            await this.subscribeService.subscribe(dummyEmail, dummyPassword, null, null, null, null, bubbleLinkCode, "none");

            // Logon with guest user credentials
            await this.authService.logon(dummyEmail, dummyPassword, rememberMe);
            this.loggerService.info(`[ConnectionService] logonWithBubbleLink (bubbleLinkCode: ${bubbleLinkCode}) -- success`);
            this.updateConnectionState(ConnectionStateEnum.CONNECTING);
            await this.startServices();
            return this.contactService.userContact;
        }
        catch (error) {
            this.loggerService.info(`[ConnectionService] logonWithBubbleLink (bubbleLinkCode: ${bubbleLinkCode}) -- failure -- ${error.message}`);
            throw error;
        }
    }

    public async setRenewedToken(token: string): Promise<void> {
        return this.authService.setRenewedToken(token);
    }

    public async logout(): Promise<void> {
        this.loggerService.info(`[ConnectionService] logout ()`);
        this.authService.token = undefined;
        localStorage.removeItem('id');
        localStorage.removeItem('token');
        localStorage.removeItem('authType');
        localStorage.removeItem('token');
        localStorage.removeItem('login');
        return this.stopServices(DisconnectionReasonEnum.UNLOGGED);
    }

    public getState(): ConnectionState {
        return this.connectionState;
    }

    public getToken(): string {
        return this.authService.token;
    }

    public getServer(): string {
        return config.restServerUrl;
    }

    private async startServices() {
        try {
            this.servicesEventListeners();
            if (!this.servicesStarted && !this.servicesStarting) {
                this.servicesStarting = true;
                this.loggerService.info('[ConnectionService] startServices ()');
                await this.executeActions(this.launchConfiguration.getStartServices());
            }
        }
        catch (error) {
            if (error instanceof RBError) this.loggerService.error(`[ConnectionService] startServices () -- failure -- ${error.message}`);
            this.servicesStarted = false;
            this.servicesStarting = false;
            this.updateConnectionState(ConnectionStateEnum.DISCONNECTED, DisconnectionReasonEnum.UNLOGGED);
            throw error;
        }
    }

    private async stopServices(reason: DisconnectionReasonEnum) {
        try {
            if (this.servicesStarted) {
                this.authService.logout();
                await this.executeActions(this.launchConfiguration?.getStopServices());
                this.servicesStarted = false;
                this.subscriptions.forEach((subscription: Subscription) => { subscription.unsubscribe(); }); this.subscriptions = [];
                this.loggerService.info('[ConnectionService] stopServices () -- success');
                this.updateConnectionState(ConnectionStateEnum.DISCONNECTED, reason);
            }
        }
        catch (error) {
            if (error instanceof RBError) this.loggerService.error(`[ConnectionService] stopServices () -- failure -- ${error.message}`);
            this.servicesStarted = false;
            throw error;
        }
    }

    private servicesEventListeners() {
        this.subscriptions.forEach((subscription: Subscription) => { subscription.unsubscribe(); }); this.subscriptions = [];
        this.subscriptions.push(this.xmppService.subscribeToConnectionEvents((event: RBEvent) => { this.onXmppConnectionEvent(event); }));
        this.subscriptions.push(this.authService.subscribe((event: RBEvent) => { this.onAuthServiceEvent(event); }));
    }

    private onXmppConnectionEvent(event: RBEvent) {
        try {
            if (this.servicesStarted || this.servicesStarting) {
                if (event.name === 'ON_XMPP_CONNECTED' && event.data.reason === 'RECONNECTION') { this.reconnectServices(); }
                else if (event.name === 'ON_XMPP_DISCONNECTED') {
                    if (event.data.reason) {
                        switch (event.data.reason) {
                            case '3RD_PARTY_CHANGE_PWD': this.stopServices(DisconnectionReasonEnum.THIRD_PARTY_CHANGE_PWD); break;
                            case 'CHANGE_PWD_ERROR': this.stopServices(DisconnectionReasonEnum.CHANGE_PWD_ERROR); break;
                            case 'NO_NETWORK': this.updateConnectionState(ConnectionStateEnum.DISCONNECTED, DisconnectionReasonEnum.NO_NETWORK); break;
                            default: break;
                        }
                    }
                }
            }
        } catch { /**/ }
    }

    private onAuthServiceEvent(event: RBEvent) {
        try {
            switch (event.name) {
                case AuthServiceEvents.RAINBOW_ON_AUTH_TOKEN_EXPIRE: this.stopServices(DisconnectionReasonEnum.AUTH_TOKEN_EXPIRE); break;
                case AuthServiceEvents.RAINBOW_ON_AUTH_TOKEN_RENEW: this.sendTokenEvent(ConnectionTokenState.AUTH_TOKEN_RENEW); break;
                case AuthServiceEvents.RAINBOW_ON_AUTH_TOKEN_WILL_EXPIRE: this.sendTokenEvent(ConnectionTokenState.AUTH_TOKEN_WILL_EXPIRE); break;
                default: break;
            }
        } catch { /**/ }
    }

    private sendTokenEvent(tokenEvent: ConnectionTokenState): void {
        this.loggerService.infoEvent(`[ConnectionService] sendEvent ${ConnectionServiceEvents.RAINBOW_ON_TOKEN_STATE_CHANGE} -- ${tokenEvent}`);
        this.eventSubject.next(RBEvent.create<ConnectionServiceEvents, ConnectionTokenState>(ConnectionServiceEvents.RAINBOW_ON_TOKEN_STATE_CHANGE, ConnectionTokenState.AUTH_TOKEN_RENEW));
    }

    private async reconnectServices(attempt: number = 0) {
        if (!this.xmppService.connected) {
            this.loggerService.warn('[ConnectionService] reconnectServices () -- we are currently disconnected');
            return;
        }

        if (attempt >= 3) {
            this.loggerService.warn('[ConnectionService] reconnectServices () -- too many attemps, go to login');
            await this.stopServices(DisconnectionReasonEnum.UNLOGGED);
            return;
        }

        try {
            this.updateConnectionState(ConnectionStateEnum.RECONNECTING);
            this.loggerService.info(`[ConnectionService] reconnectServices (attempt: ${attempt})`);
            await this.executeActions(this.launchConfiguration?.getReconnectServices());
        }
        catch (error) {
            this.loggerService.error(`[ConnectionService] reconnectServices () -- failure -- ${error.message}`);
            this.reconnectServices(++attempt);
        }
    }

    /**
     * Method executeActions
     * @param actionsList - an array of action to executes\
     * - actionsList contains several parameters \{ name, action, deps, notBlocking \}\
     * - "name" : the name of the targeted service (ex : 'webinarService') (mandatory)\
     * - "action" : the name of the method to call on the service (ex: 'start') (mandatory)\
     * - "deps" : an array of parameters (used by the "action" methods) (optional)\
     * - "notBlocking" : when set to true, even if this "action" call fails the process continue (optional, default = false)\
     * - "noWait" : when set to true asyn "actions" are not "awaited"
     * @returns a promise that resolve when all actions are executed
     */
    private async executeActions(actionsList: any[]): Promise<void> {
        const mainService = MainService.getInstance();
        const filteredList = actionsList.filter((service: any) => { return service.name !== 'ignore'; });
        const mainServicesNumber = filteredList.findIndex((service: any) => { return service.name === 'launcherService'; }) + 1;

        await filteredList.reduce(async (promise: Promise<any>, service: any, index: number) => {

            // Execute actions on services
            await promise;
            if (service.action === 'start') {
                const connectionProgress = ConnectionProgress.create(index + 1, mainServicesNumber, service.name);
                if (this.progressEvents) this.updateConnectionState(ConnectionStateEnum.CONNECTING, null, connectionProgress);
            }

            // Handle launcherService internal actions
            if (service.name === 'launcherService') {
                if (service.action === 'start' || service.action === 'reconnect') {
                    this.servicesStarted = true;
                    this.servicesStarting = false;
                    this.updateConnectionState(ConnectionStateEnum.CONNECTED);
                }
                return Promise.resolve();
            }

            const serviceObject = mainService.inject(service.name);
            if (!serviceObject) { return Promise.resolve(); }

            const deps = (service.deps)
                ? service.deps.map((dep: any) => {
                    if (typeof dep === 'string') return this[dep];
                    if (typeof dep === 'boolean') return dep;
                    if (typeof dep === 'object') return dep;
                    return null;
                })
                : null;

            try {
                const action = serviceObject[service.action];
                if (action) {
                    this.loggerService.info(`[ConnectionService] executeActions -- action ${service.action} for service ${service.name}`);
                    if (service.sync) return await action.apply(serviceObject, deps);
                    await (async () => { action.apply(serviceObject, deps); })(); return;
                }
                this.loggerService.warn(`[ConnectionService] executeActions -- no action ${service.action} for service ${service.name}`);
            }
            catch (error) {
                if (service.blocking) { throw error; }
                return Promise.resolve();
            }

        }, Promise.resolve());
    }

    private updateConnectionState(state: ConnectionStateEnum, reason?: DisconnectionReasonEnum, progress?: ConnectionProgress): void {
        if (this.connectionState.state !== state || (this.connectionState.reason !== reason || progress)) {
            this.connectionState.state = state;
            this.connectionState.reason = reason;
            this.connectionState.progress = progress;
            this.loggerService.infoEvent(`[ConnectionService] sendEvent ${ConnectionServiceEvents.RAINBOW_ON_CONNECTION_STATE_CHANGE} -- ${state}${reason ? ' -- ' + reason : ''} ${progress ? ' -- ' + progress.label : ''} `);
            this.eventSubject.next(RBEvent.create<ConnectionServiceEvents, ConnectionState>(ConnectionServiceEvents.RAINBOW_ON_CONNECTION_STATE_CHANGE, ConnectionState.create(state, reason, progress)));
        }
    }
}
